import React from 'react';
import { Check, X, Minus, Star } from 'lucide-react';
export function ComparisonTableSection() {
  const rows = [{
    label: 'Effort Required',
    sunflow: {
      text: 'Zero (Automated)',
      icon: Check,
      type: 'success'
    },
    plowing: {
      text: 'Low',
      icon: Minus,
      type: 'neutral'
    },
    shoveling: {
      text: 'High (Back-breaking)',
      icon: X,
      type: 'error'
    }
  }, {
    label: 'Reliability',
    sunflow: {
      text: '100% (Instant)',
      icon: Check,
      type: 'success'
    },
    plowing: {
      text: 'Variable (Wait times)',
      icon: Minus,
      type: 'warning'
    },
    shoveling: {
      text: 'Dependent on you',
      icon: Minus,
      type: 'warning'
    }
  }, {
    label: 'Ice Prevention',
    sunflow: {
      text: 'Excellent',
      icon: Check,
      type: 'success'
    },
    plowing: {
      text: 'Poor (Often leaves layer)',
      icon: X,
      type: 'error'
    },
    shoveling: {
      text: 'Fair (Requires salt)',
      icon: Minus,
      type: 'warning'
    }
  }, {
    label: 'Long-term Cost',
    sunflow: {
      text: 'Low Operating Cost',
      icon: Check,
      type: 'success'
    },
    plowing: {
      text: 'High (Annual contracts)',
      icon: X,
      type: 'error'
    },
    shoveling: {
      text: 'Manual Labour + Gas',
      icon: Minus,
      type: 'warning'
    }
  }, {
    label: 'Surface Damage',
    sunflow: {
      text: 'None (No chemicals)',
      icon: Check,
      type: 'success'
    },
    plowing: {
      text: 'High (Scrapes/Salt)',
      icon: X,
      type: 'error'
    },
    shoveling: {
      text: 'Medium (Salt damage)',
      icon: Minus,
      type: 'warning'
    }
  }];
  return <section className="py-16 bg-slate-50 md:py-24 overflow-hidden" data-id="element-32">
      <div className="container mx-auto px-4 md:px-6" data-id="element-33">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-34">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-35">
            The Smartest Way to Handle Winter
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-36">
            See how Sunflow automated heating compares to traditional snow
            removal methods.
          </p>
        </div>

        <div className="overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-37">
          <div className="min-w-[800px] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden relative" data-id="element-38">
            {/* Decorative background blob for Sunflow column */}
            <div className="absolute top-0 bottom-0 left-[25%] w-[25%] bg-gradient-to-b from-primary/5 to-primary/10 pointer-events-none z-0" data-id="element-39"></div>

            <table className="w-full text-sm text-left relative z-10" data-id="element-40">
              <thead data-id="element-41">
                <tr className="border-b border-slate-200" data-id="element-42">
                  <th className="px-6 py-6 w-1/4 bg-slate-50/50" data-id="element-43">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-44">
                      Comparison
                    </span>
                  </th>
                  <th className="px-6 py-6 w-1/4 bg-primary/10 border-x border-primary/20 relative overflow-hidden" data-id="element-45">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-blue-400" data-id="element-46"></div>
                    <div className="flex items-center gap-2" data-id="element-47">
                      <div className="p-1.5 rounded-full bg-primary text-white shadow-lg shadow-primary/30" data-id="element-48">
                        <Star className="h-4 w-4 fill-current" data-id="element-49" />
                      </div>
                      <span className="text-lg font-bold text-slate-900" data-id="element-50">
                        Sunflow System
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-6 w-1/4 bg-slate-50/50" data-id="element-51">
                    <span className="text-sm font-bold text-slate-700" data-id="element-52">
                      Plowing Service
                    </span>
                  </th>
                  <th className="px-6 py-6 w-1/4 bg-slate-50/50" data-id="element-53">
                    <span className="text-sm font-bold text-slate-700" data-id="element-54">
                      Shovelling / Blowing
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-55">
                {rows.map((row, index) => <tr key={index} className="group hover:bg-slate-50 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 fill-mode-backwards" style={{
                animationDelay: `${index * 100}ms`
              }} data-id="element-56">
                    <td className="px-6 py-5 font-semibold text-slate-900 group-hover:text-primary transition-colors" data-id="element-57">
                      {row.label}
                    </td>

                    {/* Sunflow Column */}
                    <td className="px-6 py-5 bg-primary/5 border-x border-primary/10 group-hover:bg-primary/10 transition-colors relative" data-id="element-58">
                      <div className="flex items-center gap-3" data-id="element-59">
                        <div className={`p-1 rounded-full ${row.sunflow.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-60">
                          <row.sunflow.icon className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" data-id="element-61" />
                        </div>
                        <span className="font-bold text-slate-900" data-id="element-62">
                          {row.sunflow.text}
                        </span>
                      </div>
                    </td>

                    {/* Plowing Column */}
                    <td className="px-6 py-5 text-slate-600" data-id="element-63">
                      <div className="flex items-center gap-3 opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-64">
                        <div className={`p-1 rounded-full ${row.plowing.type === 'success' ? 'bg-green-100 text-green-600' : row.plowing.type === 'error' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`} data-id="element-65">
                          <row.plowing.icon className="h-4 w-4" data-id="element-66" />
                        </div>
                        <span data-id="element-67">{row.plowing.text}</span>
                      </div>
                    </td>

                    {/* Shoveling Column */}
                    <td className="px-6 py-5 text-slate-600" data-id="element-68">
                      <div className="flex items-center gap-3 opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-69">
                        <div className={`p-1 rounded-full ${row.shoveling.type === 'success' ? 'bg-green-100 text-green-600' : row.shoveling.type === 'error' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`} data-id="element-70">
                          <row.shoveling.icon className="h-4 w-4" data-id="element-71" />
                        </div>
                        <span data-id="element-72">{row.shoveling.text}</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>;
}